/**
 * The test timers this tab started, kept in memory only (never in browser storage, like every other patient-related
 * value in this app), so they survive moving between screens but not a reload or a sign-out. The countdown on the
 * encounter itself is still computed from the start time saved on the server; this store adds the cues and the
 * running-timers bar. The browser side effects (sound, notification) are injected so the logic is testable.
 *
 * A read's cue comes from a timeout scheduled for its due time and from `check()`, which compares every read with the
 * wall clock: browser timers do not run while the computer sleeps or the tab is frozen, so the bar calls `check()`
 * on its one-second tick and whenever the tab comes back (visible, focused, shown, resumed). Each read cues once.
 */

/** After the last read of a running timer comes due, the session is kept this much longer before an idle sign-out may end it. */
export const TIMER_GRACE_MS = 2 * 60_000;

/**
 * However long the panel's read intervals are (they come from the server, C14), a timer holds the idle sign-out for
 * at most this long after the tray was applied, so an unattended screen does not stay signed in with a record open.
 */
export const TIMER_HOLD_MAX_MS = 30 * 60_000;

export type ReadKey = "histamine" | "full";

export interface TimerReadSpec {
  key: ReadKey;
  minutes: number;
}

export interface TimerRead extends TimerReadSpec {
  /** Epoch milliseconds. */
  dueAt: number;
  done: boolean;
}

export interface RunningTimer {
  encounterId: string;
  /** The practice the encounter belongs to: after a switch to another practice the bar must not open it under the wrong one. */
  organizationId: string;
  /** What the app already shows for this encounter (the patient's name), for the in-app bar only; never in a cue. */
  label: string;
  /** Epoch milliseconds: the application time saved on the server. */
  appliedAt: number;
  reads: TimerRead[];
  /** A read came due and nobody has opened or acknowledged it since: drives the visual cue. */
  alerting: boolean;
}

/** What a read coming due does outside the page: sound and a notification, both free of any patient detail. */
export interface TimerCues {
  due(read: { key: ReadKey; minutes: number; tag: string }): void;
}

export interface TimerStore {
  start(input: { encounterId: string; organizationId: string; label: string; appliedAt: number; reads: TimerReadSpec[] }): void;
  /** Cues every read whose time has come by the wall clock and has not been cued yet (after sleep, a frozen tab, a late timeout). */
  check(): void;
  /** Clears the visual cue for an encounter (opened, or acknowledged on the bar). */
  acknowledge(encounterId: string): void;
  /** Removes an encounter from the bar and cancels its pending cues. */
  dismiss(encounterId: string): void;
  clear(): void;
  get(encounterId: string): RunningTimer | undefined;
  /** An immutable snapshot, replaced on every change (for useSyncExternalStore). */
  list(): readonly RunningTimer[];
  /** Whether any read is still pending. */
  hasRunning(): boolean;
  /** The latest due time on the bar plus the grace (each timer capped at TIMER_HOLD_MAX_MS after application), or null with nothing on it: the idle sign-out waits for it. */
  holdUntil(): number | null;
  subscribe(listener: () => void): () => void;
}

type Handle = ReturnType<typeof setTimeout>;

export function createTimerStore(deps: { now?: () => number; schedule?: (fn: () => void, ms: number) => Handle; cancel?: (h: Handle) => void; cues?: TimerCues; graceMs?: number; holdMaxMs?: number } = {}): TimerStore {
  const now = deps.now ?? (() => Date.now());
  const schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms));
  const cancel = deps.cancel ?? ((h) => clearTimeout(h));
  const graceMs = deps.graceMs ?? TIMER_GRACE_MS;
  const holdMaxMs = deps.holdMaxMs ?? TIMER_HOLD_MAX_MS;
  const entries = new Map<string, { timer: RunningTimer; handles: Handle[]; tagBase: string }>();
  const listeners = new Set<() => void>();
  let snapshot: readonly RunningTimer[] = [];
  let seq = 0;

  const hasRunning = () => [...entries.values()].some((e) => e.timer.reads.some((r) => !r.done));

  function emit() {
    snapshot = [...entries.values()].map((e) => e.timer).sort((a, b) => a.appliedAt - b.appliedAt);
    for (const l of listeners) l();
  }

  function update(encounterId: string, change: (t: RunningTimer) => RunningTimer) {
    const entry = entries.get(encounterId);
    if (!entry) return;
    entry.timer = change(entry.timer);
    emit();
  }

  /** Marks one read due and cues it, once: the timeout and `check()` may both get here. */
  function fire(encounterId: string, key: ReadKey) {
    const entry = entries.get(encounterId);
    const read = entry?.timer.reads.find((r) => r.key === key);
    if (!entry || !read || read.done) return;
    update(encounterId, (t) => ({ ...t, alerting: true, reads: t.reads.map((x) => (x.key === key ? { ...x, done: true } : x)) }));
    deps.cues?.due({ key, minutes: read.minutes, tag: `${entry.tagBase}-${read.minutes}` });
  }

  function remove(encounterId: string) {
    const entry = entries.get(encounterId);
    if (!entry) return false;
    entry.handles.forEach(cancel);
    entries.delete(encounterId);
    return true;
  }

  return {
    start({ encounterId, organizationId, label, appliedAt, reads }) {
      remove(encounterId);
      const at = now();
      // A tag per start, not the encounter id: nothing that identifies the patient or the record reaches the OS.
      const tagBase = `a123-timer-${++seq}`;
      const timer: RunningTimer = { encounterId, organizationId, label, appliedAt, alerting: false, reads: reads.map((r) => ({ ...r, dueAt: appliedAt + r.minutes * 60_000, done: appliedAt + r.minutes * 60_000 <= at })) };
      const handles = timer.reads.filter((r) => !r.done).map((r) => schedule(() => fire(encounterId, r.key), r.dueAt - at));
      entries.set(encounterId, { timer, handles, tagBase });
      emit();
    },
    check() {
      const at = now();
      for (const [encounterId, { timer }] of [...entries]) for (const r of timer.reads) if (!r.done && r.dueAt <= at) fire(encounterId, r.key);
    },
    acknowledge(encounterId) {
      if (entries.get(encounterId)?.timer.alerting) update(encounterId, (t) => ({ ...t, alerting: false }));
    },
    dismiss(encounterId) {
      if (remove(encounterId)) emit();
    },
    clear() {
      if (entries.size === 0) return;
      for (const id of [...entries.keys()]) remove(id);
      emit();
    },
    get: (encounterId) => entries.get(encounterId)?.timer,
    list: () => snapshot,
    hasRunning,
    holdUntil() {
      let hold: number | null = null;
      for (const { timer } of entries.values()) {
        if (timer.reads.length === 0) continue;
        const until = Math.min(Math.max(...timer.reads.map((r) => r.dueAt)) + graceMs, timer.appliedAt + holdMaxMs);
        hold = Math.max(hold ?? until, until);
      }
      return hold;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** The read's name on screen. */
export const READ_LABELS: Record<ReadKey, string> = { histamine: "Histamine read", full: "All reactions" };

/**
 * The OS notification for a read that came due. Generic on purpose: a lock screen or notification centre may show it
 * to anyone, so it names no patient, record or location (AGENTS.md), only that a timer is done and its interval.
 */
export function notificationFor(read: { key: ReadKey; minutes: number }): { title: string; body: string } {
  return { title: "A test timer is done", body: `${read.minutes}-minute ${read.key === "histamine" ? "histamine" : "full"} read is due.` };
}

/**
 * What a screen reader hears when reads come due (the bar's status line). Speech goes through the room's speakers,
 * like a sound, so it names no patient (AGENTS.md); the names are on the bar for whoever moves to it.
 */
export function dueAnnouncement(alerting: number): string {
  if (alerting === 0) return "";
  return alerting === 1 ? "A test timer is done and a read is due. See the running test timers at the foot of the page." : `${alerting} test timers are done and reads are due. See the running test timers at the foot of the page.`;
}

/** mm:ss for a non-negative duration. */
export function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}
